<template>
    <div class="day-picker">
        <div v-for="(it, id) in days" :key="'day_' + id"
             class="day">
            {{ it }}
        </div>
        <div v-for="(it, id) in dates" :key="'date_' + id"
             :class="{
                 today: it.today,
                 disabled: it.disabled,
                 range: it.range,
                 start: it.start,
                 end:   it.end,
                 selected: it.selected,
                 holiday:  it.holiday,
             }"
             :title="it.hint"
             class="date"
             @click="select(it)"
             @mouseenter="$emit('hoverDate', it)"
             @mouseleave="$emit('hoverDate', null)"
        >
            <div class="holder">
                {{ it.title }}
            </div>
        </div>
    </div>
</template>

<script>
import { toMap } from '@dsee/util/arrays';
import { DAY_OF_WEEK, equalDates, generateDays, sortIfNeeded } from '../../util.js';

const ZONE_OFFSET = new Date().getTimezoneOffset() * 60 * 1000;
const calendarCache = {};

export default {
    props: {
        value: {
            // Date | Array<Date>
        },
        month: {
            type: Number,
            required: true,
        },
        year: {
            type: Number,
            required: true,
        },
        minDate: {
            type: Date,
            required: true,
        },
        maxDate: {
            type: Date,
            required: true,
        },
        dateDisabled: {
            type: Function,
            default: (it) => false,
        },
        dualPicker: {
            type: Boolean,
            required: true,
        },
        selectRange: {
            type: Boolean,
            default: false,
        },
        hoverDate: {
            type: Date,
        },
        hoverForward: {
            type: Boolean,
        },
        calendarRegion: {
            type: String,
            default: 'RU',
        },
        calendarLoader: {
            type: Function,
            default: (region, year) => [],
        },
    },
    computed: {
        regionCalendar() {
            let result = calendarCache[this.calendarRegion];

            if (result == null)
                result = calendarCache[this.calendarRegion] = {};

            return result;
        },
        days() {
            return DAY_OF_WEEK;
        },
        selected: {
            get() {
                if (!Array.isArray(this.value))
                    return this.value;

                return sortIfNeeded(this.value.slice(0, 2));
            },
            set(value) {
                this.$emit('input', value);
            },
        },
    },
    data() {
        return {
            dates: [],
        };
    },
    methods: {
        select(date) {
            if (date.disabled)
                return;

            const value = new Date(date.date);

            if (!this.selectRange) {
                this.selected = value;
                return;
            }

            const result = (this.selected ?? []).slice();
            result.length = 2;

            const index = result.findIndex(it => it == null);
            if (index < 0) {
                result[0] = value;
                result[1] = null;
            } else {
                result[index] = value;
            }

            this.selected = sortIfNeeded(result);
        },
        isSelected(date) {
            if (this.selected == null)
                return false;

            if (!Array.isArray(this.selected))
                return equalDates(this.selected, date);

            return this.selected.some((it) => equalDates(it, date));
        },
        inRange(date) {
            if (this.selected == null)
                return false;

            if (!Array.isArray(this.selected))
                return false;

            if (this.selected[0] && this.selected[1]) {
                return date >= this.selected[0] && date <= this.selected[1];
            }

            const firstDate = this.selected[0] || this.selected[1];

            if (!this.hoverDate || !firstDate)
                return false;

            if (this.hoverForward) {
                return date >= firstDate && date <= this.hoverDate;
            } else {
                return date >= this.hoverDate && date <= firstDate;
            }
        },
        async generateCalendar() {
            const now = new Date();
            const result = [];

            for (const it of generateDays(this.year, this.month)) {
                const item = {
                    title: it.getDate(),
                    today: equalDates(now, it),
                    date: it,
                    disabled: false,
                    range: false,
                    start: false,
                    end: false,
                    selected: false,
                    holiday: await this.isHoliday(it),
                };

                result.push(item);
            }

            this.dates = result;
        },
        async isHoliday(date) {
            const month = date.getMonth();
            if (month !== this.month)
                return false;

            const year = date.getFullYear();

            let yearCalendar = this.regionCalendar[year];
            if (yearCalendar == null)
                yearCalendar = this.regionCalendar[year] = this.prepareCalendar(year);

            const calendar = await yearCalendar;
            const value = calendar[date.getTime()];

            if (value === 'HOLIDAY')
                return true;

            if (value === 'WORKDAY')
                return false;

            const day = date.getDay();
            return day === 6 || day === 0;
        },
        async prepareCalendar(year) {
            const resp = await this.calendarLoader(this.calendarRegion, year);
            return toMap(resp, (it) => Date.parse(it.day) + ZONE_OFFSET, (it) => it.type);
        },
        markCalendar() {
            for (const item of this.dates) {
                const date = item.date;

                item.disabled = this.dateDisabled(date)
                    || date.getMonth() !== this.month
                    || date < this.minDate
                    || date > this.maxDate;

                item.range = item.disabled ? false : this.inRange(date);

                if (item.disabled) {
                    item.hint = 'Дата недоступна для выбора';
                } else {
                    item.hint = item.holiday ? 'Выходной день' : 'Выбрать дату';
                }

                item.selected = this.isSelected(date);

                if (item.selected) {
                    item.start = equalDates(this.selected[0], date);
                    item.end = equalDates(this.selected[1], date);
                }
            }
        },
    },
    created() {
        this.$options.unwatchGenerateCalendar = this.$watch((vm) => [
            vm.year,
            vm.month,
        ], this.generateCalendar, {
            immediate: true,
        });

        this.$options.unwatchMarkCalendar = this.$watch((vm) => [
            vm.dates,
            vm.selected,
            vm.minDate,
            vm.maxDate,
            vm.dateDisabled,
            vm.hoverDate,
            vm.hoverForward,
        ], this.markCalendar, {
            immediate: true,
        });
    },
    beforeDestroy() {
        this.$options.unwatchGenerateCalendar();
        this.$options.unwatchMarkCalendar();
    },
};
</script>

<style lang="scss" scoped>
.day-picker {
    display: grid;
    grid-template-columns: repeat(7, 1fr);

    .day {
        text-align: center;
        font-size: 12px;
        line-height: 16px;
        color: rgba(16, 24, 40, 0.5);
        height: 36px;
    }

    .date {
        text-align: center;
        color: rgba(16, 24, 40, 0.7);
        font-weight: 500;
        cursor: pointer;
        height: 36px;
        line-height: 36px;

        &.today {
            color: var(--option-item-active-background);
        }

        &.holiday {
            background: #f0f6ff;
        }

        &.start {
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
        }

        &.end {
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
        }

        &.selected {
            .holder {
                background: var(--option-item-active-background);
                color: #FFFFFF;
                border-radius: 4px;
            }
        }

        &.start {
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
        }

        &.end {
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
        }

        &.selected {
            .holder {
                background: var(--option-item-active-background);
                color: #FFFFFF;
                border-radius: 4px;
            }
        }

        &.disabled {
            color: rgba(16, 24, 40, 0.25);
            cursor: not-allowed;
        }

        &.range {
            background: var(--option-item-hover-background);
            color: rgba(16, 24, 40, 0.7);
        }

        &:not(.disabled):not(.selected):not(.range):hover {
            border-radius: 4px;
            background: var(--option-item-hover-background);
        }
    }
}
</style>
